import {
  autoUpdate,
  ElementProps,
  flip,
  FloatingContext,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Color } from '@persona-id/design-tokens';
import { useFocusRing } from '@react-aria/focus';
import { useSliderThumb } from '@react-aria/slider';
import { mergeProps } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { SliderState } from '@react-stately/slider';
import { debounce } from 'lodash';
import { OutputHTMLAttributes, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { FloatingTooltip } from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';

const Tooltip = styled(FloatingTooltip)`
  ${paragraphMixins.sm};
`;

const Thumb = styled('div')<{ disabled?: boolean }>`
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  background: ${(props) =>
    props.disabled ? Color.BackgroundInputDisabled : Color.BackgroundInputDefault};
  cursor: ${(props) => (props.disabled ? 'inherit' : 'pointer')};
  border-radius: 50%;
  border: 1px solid ${Color.BorderInputDisabled};
  /* Enable clicks on thumb */
  pointer-events: auto;
`;

interface SliderThumbProps {
  state: SliderState;
  trackRef: RefObject<HTMLElement>;
  index: number;
  outputProps: OutputHTMLAttributes<HTMLOutputElement>;
  hideTooltips?: boolean;
}

interface UsePressedOptions {
  delay?: number;
}

const usePressed = (context: FloatingContext, options?: UsePressedOptions): ElementProps => {
  const closeHandler = useMemo(
    () => debounce(() => context.onOpenChange(false), options?.delay ?? 500),
    [],
  );
  return {
    reference: {
      onPointerDown: () => {
        context.onOpenChange(true);
      },
      onPointerUp: () => {
        closeHandler();
      },
    },
  };
};

const SliderThumb = ({ state, trackRef, index, outputProps, hideTooltips }: SliderThumbProps) => {
  const inputRef = useRef(null);
  const { thumbProps, inputProps } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
    },
    state,
  );

  const { focusProps, isFocused } = useFocusRing();

  const [open, setOpen] = useState(false);

  const { x, y, refs, strategy, context } = useFloating({
    placement: 'top',
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 4 })],
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
    usePressed(context),
  ]);

  useEffect(() => {
    setOpen(isFocused);
  }, [isFocused]);

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        left: `${state.getThumbPercent(index) * 100}%`,
      }}
    >
      {open && !hideTooltips && (
        <Tooltip
          {...mergeProps(
            getFloatingProps({
              ref: refs.setFloating,
              style: {
                position: strategy,
                top: y ?? '',
                left: x ?? '',
              },
            }),
            outputProps,
          )}
        >
          {state.getThumbValueLabel(index)}
        </Tooltip>
      )}
      <Thumb
        {...mergeProps(
          thumbProps,
          getReferenceProps({
            ref: refs.setReference,
          }),
        )}
      >
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </Thumb>
    </div>
  );
};

export default SliderThumb;
